import { FC, ReactElement, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import If from '@/storybook/components/If/If';
import { ColumnDisplayItem, MAX_PINNED_COLUMNS } from '@/types/table/columnConfig';
import ColumnConfigItem from './ColumnConfigItem';
import styles from './PinningTab.module.css';

type PinningUnitChild = { id: string; label: string };

type PinningUnit = {
  id: string;
  label: string;
  columnIds: string[];
  children: PinningUnitChild[];
};

const stripGroupPrefix = (label: string, groupLabel: string): string => {
  const groupPrefix = `${groupLabel}: `;

  return label.startsWith(groupPrefix) ? label.slice(groupPrefix.length) : label;
};

const buildPinningUnits = (columnItems: ColumnDisplayItem[]): PinningUnit[] => {
  const pinningUnits: PinningUnit[] = [];
  const unitByGroup = new Map<string, PinningUnit>();

  for (const columnItem of columnItems) {
    const { parentGroup } = columnItem;

    if (!parentGroup) {
      pinningUnits.push({ id: columnItem.id, label: columnItem.label, columnIds: [columnItem.id], children: [] });
    } else {
      const groupChild: PinningUnitChild = {
        id: columnItem.id,
        label: stripGroupPrefix(columnItem.label, parentGroup),
      };
      const existingUnit = unitByGroup.get(parentGroup);

      if (existingUnit) {
        existingUnit.columnIds.push(columnItem.id);
        existingUnit.children.push(groupChild);
      } else {
        const newGroupUnit: PinningUnit = {
          id: `group:${parentGroup}`,
          label: parentGroup,
          columnIds: [columnItem.id],
          children: [groupChild],
        };
        unitByGroup.set(parentGroup, newGroupUnit);
        pinningUnits.push(newGroupUnit);
      }
    }
  }

  return pinningUnits;
};

interface PinningTabProps {
  pinnedItems: ColumnDisplayItem[];
  unpinnedItems: ColumnDisplayItem[];
  onTogglePinGroup: (columnIds: string[]) => void;
}

const PinningTab: FC<PinningTabProps> = ({ pinnedItems, unpinnedItems, onTogglePinGroup }) => {
  const { t } = useTranslation();
  const pinnedUnits = useMemo(() => buildPinningUnits(pinnedItems), [pinnedItems]);
  const unpinnedUnits = useMemo(() => buildPinningUnits(unpinnedItems), [unpinnedItems]);
  const hasPinnedUnits = pinnedUnits.length > 0;
  const pinLimitTooltip = t('global.table.columnConfig.pinLimitReached', { max: MAX_PINNED_COLUMNS });

  const renderUnitRows = (pinningUnit: PinningUnit, isUnitPinned: boolean): ReactElement[] => {
    const canPinUnit = isUnitPinned || pinnedUnits.length + 1 <= MAX_PINNED_COLUMNS;

    const parentRow = (
      <ColumnConfigItem
        key={pinningUnit.id}
        id={pinningUnit.id}
        label={pinningUnit.label}
        isPinned={isUnitPinned}
        canPin={canPinUnit}
        showPinButton
        onTogglePin={() => onTogglePinGroup(pinningUnit.columnIds)}
        pinTooltip={!isUnitPinned && !canPinUnit ? pinLimitTooltip : undefined}
      />
    );

    const childRows = pinningUnit.children.map(groupChild => (
      <ColumnConfigItem key={groupChild.id} id={groupChild.id} label={groupChild.label} isChild />
    ));

    return [parentRow, ...childRows];
  };

  return (
    <div className={styles.container} data-testid="pinning-tab">
      <If condition={hasPinnedUnits}>
        <div className={styles.pinnedSection}>
          {pinnedUnits.flatMap(pinnedUnit => renderUnitRows(pinnedUnit, true))}
        </div>
        <div className={styles.divider} />
      </If>

      <div className={styles.columnList}>
        {unpinnedUnits.flatMap(unpinnedUnit => renderUnitRows(unpinnedUnit, false))}
      </div>
    </div>
  );
};

export default PinningTab;
